import type { SectionEditorSettings, SettingsComponentProps } from '~/clc/features/theming/types';
import type { HubFooter } from '../../../constants';
import { Body, Divider, Span, Switch } from '@workramp/ui';
import { CLCHubSectionTypes } from '~/clc/features/theming/constants';
import { MIXPANEL_COMMUNITIES_WR_BRANDING_TOGGLED } from '~/clc/features/community/constants';
import { MixPanel } from '~/common';
import { ColorSettings } from '../../../admin/components/sections/colors';
import { CustomCSSSetting, MenuSetting, SettingsSidebarSubSection, TextAreaSetting } from '../../../admin/components';
import { CollapsibleSetting } from '../../../admin/components/ThemingEditorPage/SettingsSidebar/CollapsibleSetting';

// This is the admin page
interface SettingsProps extends SettingsComponentProps {
  sectionThemeSettings: HubFooter['footer'];
}

const FooterSetting = ({ onChange, sectionThemeSettings, mixpanelInfo }: SettingsProps) => {
  const onFooterPropChange = (propName: string, val: any) => {
    onChange({ footer: { [propName]: val } });
  };

  return (
    <>
      <SettingsSidebarSubSection>
        <MenuSetting
          activeProducts={['academy', 'community']}
          mixpanelInfo={mixpanelInfo}
          value={sectionThemeSettings.menuItems}
          onChange={(links) =>
            onFooterPropChange(
              'menuItems',
              links.map(({ label, href, openInNewTab }) => ({ label, href, openInNewTab })),
            )
          }
          label="Menu"
        />
      </SettingsSidebarSubSection>
      <Divider />
      <SettingsSidebarSubSection>
        <Switch
          isSelected={sectionThemeSettings.workrampBranding}
          onChange={(val) => {
            MixPanel.track(MIXPANEL_COMMUNITIES_WR_BRANDING_TOGGLED, { toggled: val });
            onFooterPropChange('workrampBranding', val);
          }}
        >
          WorkRamp branding
        </Switch>
        <TextAreaSetting
          label="Footer text"
          value={sectionThemeSettings.footerText}
          onChange={(val) => onFooterPropChange('footerText', val)}
          maxLength={200}
        />
      </SettingsSidebarSubSection>
      <Divider />

      <CollapsibleSetting
        iconName="colors:regular"
        settingName="Colors"
        description={
          <Body level="md">Override theme defaults. You can edit your default colors in theme settings.</Body>
        }
      >
        <ColorSettings
          isLockable
          colorOptions={sectionThemeSettings.colors}
          onChange={(propName: string, val: string) => {
            onFooterPropChange('colors', { [propName]: { value: val } });
          }}
          onLockChange={(propName: string, propVal: Record<string, any>) =>
            onFooterPropChange('colors', {
              [propName]: propVal,
            })
          }
        />
      </CollapsibleSetting>

      <Divider />

      <CollapsibleSetting
        iconName="html:regular"
        settingName="Custom CSS"
        description={
          <Body level="md">
            Add custom css styles to <Span weight="bold">this section only</Span>
          </Body>
        }
      >
        <CustomCSSSetting
          label="custom css"
          hideLabel
          value={sectionThemeSettings.custom_css}
          onChange={(val) => onFooterPropChange('custom_css', val)}
        />
      </CollapsibleSetting>
    </>
  );
};

export const FooterSectionEditorSettings: SectionEditorSettings = {
  type: CLCHubSectionTypes.FOOTER,
  schemaKey: 'footer',
  name: 'Footer',
  iconName: 'layout-bottom:regular',
  settings: FooterSetting,
  description: 'Make changes to your footer. Changes here will be shared across all your pages.',
};
